import { useState } from 'react'
import { t } from 'i18next'
import { AxiosError } from 'axios'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
  InputGroup,
  Alert
} from '@/common'
import { useSession } from '@/shared/hooks/useSession'
import { useSendEmailVerificationCode, useVerifyEmailWithCode } from '@/shared/hooks/api/user'
import { toast } from 'react-hot-toast'

const EmailVerificationPage = ({
  initialState,
  next
}: {
  initialState: { verificationCode: string }
  next: (payload: { verificationCode: string }) => void
}) => {
  const [resendCodeButtonDisabled, setResendCodeButtonDisabled] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [verificationCode, setVerificationCode] = useState(initialState.verificationCode)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const { mutateAsync: sendEmailVerificationCode, isPending: isSendingEmailVerificationCode } =
    useSendEmailVerificationCode()
  const { mutateAsync: verifyEmailWithCode, isPending: isVerifiyingEmailWithCode } =
    useVerifyEmailWithCode()
  const isLoading = isVerifiyingEmailWithCode || isSendingEmailVerificationCode
  const {
    session: { email, isEmailVerified }
  } = useSession()

  const handleSendCode = async () => {
    setVerificationError(null)
    try {
      await sendEmailVerificationCode()
      toast.success(t('onboarding:emailVerification.toast.codeSent'))
      setResendCodeButtonDisabled(true)
      setCountdown(30)
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer)
            setResendCodeButtonDisabled(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (error) {
      toast.error(t('onboarding:emailVerification.toast.genericError'))
      console.error('Error sending verification code:', error)
    }
  }

  const handleVerifyCode = async () => {
    if (isEmailVerified) {
      return next({
        verificationCode
      })
    }
    setVerificationError(null)
    try {
      await verifyEmailWithCode(verificationCode)
      next({
        verificationCode
      })
    } catch (error) {
      let errorMessage = t('onboarding:emailVerification.genericError')
      if (error instanceof AxiosError) {
        if (error.response?.data.message === 'INVALID_VERIFICATION_CODE') {
          errorMessage = t('onboarding:emailVerification.invalidCodeError')
        } else if (error.response?.data.message === 'VERIFICATION_CODE_EXPIRED') {
          errorMessage = t('onboarding:emailVerification.expiredCodeError')
        }
      }
      setVerificationError(errorMessage)
    }
  }

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle title={t('onboarding:steps.emailVerification')} />
        </CardHeader>
        {!isEmailVerified ? (
          <CardBody className="space-y-4">
            <Alert type="info">{t('onboarding:emailVerification.info')}</Alert>
            <div className="grid grid-cols-2 gap-2 items-end">
              <InputGroup label={t('onboarding:inputFields.email.label')} value={email} disabled />
              <Button
                onClick={handleSendCode}
                disabled={isLoading || resendCodeButtonDisabled}
                isLoading={isLoading}
                variant="subtle"
              >
                {resendCodeButtonDisabled
                  ? t('onboarding:emailVerification.actions.resendCodeIn', { countdown })
                  : t('onboarding:emailVerification.actions.resendCode')}
              </Button>
              <InputGroup
                type="text"
                label={t('onboarding:emailVerification.codeInputLabel')}
                value={verificationCode}
                disabled={isLoading}
                onChange={evt => setVerificationCode(evt.target.value)}
              />
            </div>
            {verificationError && <Alert type="error">{verificationError}</Alert>}
          </CardBody>
        ) : (
          <CardBody>
            <p>{t('onboarding:emailVerification.success')}</p>
          </CardBody>
        )}
      </Card>
      <div className="flex justify-end">
        <Button
          className="mt-4 w-24"
          onClick={handleVerifyCode}
          disabled={isLoading || (!verificationCode && !isEmailVerified)}
        >
          {t('common:next')}
        </Button>
      </div>
    </>
  )
}

export default EmailVerificationPage
